import {
  UnavailabilityPrestatereAddBeautyInput,
  UnavailabilityPrestatereAddOutput,
  UnavailabilityPrestatereDeleteBeautyInput,
  UnavailabilityPrestatereDeleteBeautyOutput,
  UnavailabilityPrestatereGetBeautyInput,
  UnavailabilityPrestatereGetOutput,
  UnavailabilityPrestatereTable,
} from "@okeys/shared-utils"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { fetchUserAttributes } from "aws-amplify/auth"
import { setOpenSnackbar } from "../store/snackbarReducers/snackbarSlice"
import { useAppDispatch } from "../store/store"
import { customAxios } from "../utils/customAxios/customAxios"
import { getToken } from "../utils/functions/getToken"

// Hook pour récupérer les indisponibilités prestataire
export const useUnavailabilityPrestatereQuery = (prestatereId?: string) => {
  const { data, isLoading, isError, isSuccess, refetch } = useQuery({
    queryKey: ["unavailabilityPrestatere", prestatereId],
    queryFn: async () => {
      const token = await getToken()
      const queryParams: UnavailabilityPrestatereGetBeautyInput = {
        ...(prestatereId && { prestatereId }),
      }

      const dataReturn = await customAxios.get("/unavailability-prestatere", {
        params: queryParams,
        headers: {
          Authorization: token,
        },
      })
      return dataReturn.data as UnavailabilityPrestatereGetOutput
    },
  })

  return {
    unavailabilityPrestatere: data,
    isLoadingUnavailabilityPrestatere: isLoading,
    errorUnavailabilityPrestatere: isError,
    successUnavailabilityPrestatere: isSuccess,
    refetchUnavailabilityPrestatere: refetch,
  }
}

// Hook pour ajouter une indisponibilité prestataire
export const useAddUnavailabilityPrestatereMutation = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  const mutation = useMutation({
    mutationFn: async (
      unavailabilityPrestatere: UnavailabilityPrestatereAddBeautyInput
    ) => {
      const token = await getToken()
      const userAttributes = await fetchUserAttributes()
      const shopId = userAttributes["custom:shopId"]

      // Optimistic update - ajouter immédiatement à la liste
      const tempId = `temp-${Date.now()}`
      const optimisticData: UnavailabilityPrestatereTable = {
        id: tempId,
        shopId: shopId || "",
        prestatereId: unavailabilityPrestatere.prestatereId,
        startDate: unavailabilityPrestatere.startDate,
        endDate: unavailabilityPrestatere.endDate,
        processingDate: new Date().toISOString(),
        status: unavailabilityPrestatere.status || "pending",
        reason: unavailabilityPrestatere.reason,
      }

      // Mettre à jour le cache pour toutes les queries concernées
      queryClient.setQueryData(
        ["unavailabilityPrestatere", unavailabilityPrestatere.prestatereId],
        (oldData: UnavailabilityPrestatereTable[] | undefined) => {
          return [...(oldData || []), optimisticData]
        }
      )

      queryClient.setQueryData(
        ["unavailabilityPrestatere", undefined],
        (oldData: UnavailabilityPrestatereTable[] | undefined) => {
          return [...(oldData || []), optimisticData]
        }
      )

      const dataReturn = await customAxios.post(
        "/unavailability-prestatere",
        unavailabilityPrestatere,
        {
          headers: {
            Authorization: token,
          },
        }
      )
      return dataReturn.data as UnavailabilityPrestatereAddOutput
    },
    onSuccess: () => {
      // Invalider les queries pour récupérer les vraies données du serveur
      queryClient.invalidateQueries({ queryKey: ["unavailabilityPrestatere"] })
      dispatch(
        setOpenSnackbar({
          isOpen: true,
          message: "Indisponibilité prestataire ajoutée avec succès",
          severity: "success",
        })
      )
    },
    onError: () => {
      // En cas d'erreur, invalider pour revenir à l'état précédent
      queryClient.invalidateQueries({ queryKey: ["unavailabilityPrestatere"] })
      dispatch(
        setOpenSnackbar({
          isOpen: true,
          message: "Erreur lors de l'ajout de l'indisponibilité prestataire",
          severity: "error",
        })
      )
    },
  })

  return mutation
}

// Hook pour supprimer une indisponibilité prestataire
export const useDeleteUnavailabilityPrestatereMutation = () => {
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  const mutation = useMutation({
    mutationFn: async ({
      id,
      prestatereId,
    }: {
      id: string
      prestatereId?: string
    }) => {
      const token = await getToken()
      const data: UnavailabilityPrestatereDeleteBeautyInput = {
        id,
      }

      // Optimistic update - supprimer immédiatement de toutes les listes concernées
      queryClient.setQueryData(
        ["unavailabilityPrestatere", prestatereId],
        (oldData: UnavailabilityPrestatereTable[] | undefined) => {
          return (
            oldData?.filter((unavailability) => unavailability.id !== id) || []
          )
        }
      )

      queryClient.setQueryData(
        ["unavailabilityPrestatere", undefined],
        (oldData: UnavailabilityPrestatereTable[] | undefined) => {
          return (
            oldData?.filter((unavailability) => unavailability.id !== id) || []
          )
        }
      )

      const dataReturn = await customAxios.delete(
        "/unavailability-prestatere",
        {
          data,
          headers: {
            Authorization: token,
          },
        }
      )
      return dataReturn.data as UnavailabilityPrestatereDeleteBeautyOutput
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["unavailabilityPrestatere"] })
      dispatch(
        setOpenSnackbar({
          isOpen: true,
          message: "Indisponibilité prestataire supprimée",
          severity: "success",
        })
      )
    },
    onError: () => {
      // En cas d'erreur, invalider pour restaurer l'état précédent
      queryClient.invalidateQueries({ queryKey: ["unavailabilityPrestatere"] })
      dispatch(
        setOpenSnackbar({
          isOpen: true,
          message:
            "Erreur lors de la suppression de l'indisponibilité prestataire",
          severity: "error",
        })
      )
    },
  })

  return mutation
}
